import { createBlankPresetFieldMap } from '../../../presets/blankPresetFieldMap'
import {
  modifyBlankPresetPageDefinition,
  modifyRegularPresetPageDefinition,
} from './dataFixers'
import { modifyEditPage } from './edit-page-data-fixers'
import { installAppsInPage } from '../../appInstallation'
import { PAGE_TYPES, parseUrlPattern } from '@wix/wix-data-client-common'
import type { EDITOR_TYPE, PageType } from '@wix/wix-data-client-common'
import type { EditorSDK, Logger } from '@wix/wix-data-client-common-standalone'
import type { PageDefinition } from '../../../presets/types'
import type { Schema } from '@wix/wix-data-schema-types'

const getRouterDatasetName = ({
  pageType,
  collectionName,
  isEditPage = false,
}: {
  pageType: PageType
  collectionName: string
  isEditPage?: boolean
}) =>
  isEditPage
    ? `${collectionName} Manage Item`
    : pageType === PAGE_TYPES.ITEM
      ? `${collectionName} Item`
      : collectionName

export const applyDataFixers = async ({
  collectionId,
  page,
  pageType,
  presetId,
  editorType,
  fields,
  displayFieldKey,
  itemPageLinkFieldKey,
  indexPageLinkFieldKey,
  editorSdkProxy,
  pattern,
  logger,
  isFromExistingCollection,
}: {
  collectionId: string
  page: PageDefinition
  pageType: PageType
  presetId: string
  editorType: EDITOR_TYPE
  fields: Schema['fields']
  displayFieldKey: string
  itemPageLinkFieldKey: string
  indexPageLinkFieldKey: string
  editorSdkProxy: EditorSDK
  pattern: string
  logger: Logger
  isFromExistingCollection?: boolean
}) => {
  switch (presetId) {
    case 'blank':
      return {
        createPreset: ({ collectionName }: { collectionName: string }) => {
          const routerDatasetName = getRouterDatasetName({
            pageType,
            collectionName,
          })

          const { fields: patternFields } = parseUrlPattern(pattern)
          const pageLinkFields = patternFields.map(
            patternField => fields[patternField],
          )

          const fieldMap = createBlankPresetFieldMap({
            editorType,
            fields,
            itemPageLinkFieldKey,
            indexPageLinkFieldKey,
          })

          return modifyBlankPresetPageDefinition({
            initialDefinition: page.definition,
            fieldMap,
            routerDatasetName,
            pageLinkFields,
            collectionName,
            pageType,
            isFromExistingCollection,
          })
        },
      }

    case 'edit':
      return {
        createPreset: ({ collectionName }: { collectionName: string }) =>
          modifyEditPage({
            page: page.definition,
            collectionId,
            fields,
            displayFieldKey,
            pageLinkFieldKey: itemPageLinkFieldKey,
            routerDatasetName: getRouterDatasetName({
              pageType,
              collectionName,
              isEditPage: true,
            }),
          }),
      }

    default: {
      const installedAppIds = await installAppsInPage(
        editorSdkProxy,
        page,
        presetId,
        logger,
      )
      return {
        createPreset: ({
          collectionName,
          routerId,
        }: {
          collectionName: string
          routerId: string
        }) => {
          const routerDatasetName = getRouterDatasetName({
            pageType,
            collectionName,
          })

          const pageLinkFieldKey =
            pageType === PAGE_TYPES.ITEM
              ? indexPageLinkFieldKey
              : itemPageLinkFieldKey

          return modifyRegularPresetPageDefinition({
            initialDefinition: page.definition,
            pageLinkFieldKey,
            routerDatasetName,
            routerId,
            installedAppIds,
          })
        },
        installedAppIds,
      }
    }
  }
}

export type DataFixer = Awaited<ReturnType<typeof applyDataFixers>>
